import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { createStore, get, set, del, clear } from 'idb-keyval';
import type { Query } from '@tanstack/react-query';

const DB_NAME = 'getfacade-query-cache';
const STORE_NAME = 'v1';
const CACHE_KEY = 'queries';

const idbStore =
  typeof indexedDB !== 'undefined' ? createStore(DB_NAME, STORE_NAME) : null;

const asyncStorage = {
  getItem: async (key: string) => {
    if (!idbStore) return null;
    const value = await get<string>(key, idbStore);
    return value ?? null;
  },
  setItem: async (key: string, value: string) => {
    if (!idbStore) return;
    await set(key, value, idbStore);
  },
  removeItem: async (key: string) => {
    if (!idbStore) return;
    await del(key, idbStore);
  },
};

export const queryPersister = createAsyncStoragePersister({
  storage: asyncStorage,
  key: CACHE_KEY,
  throttleTime: 1000,
});

/**
 * Query keys that must NEVER be persisted to disk.
 * Auth/billing/subscription/payments change often and are security-sensitive —
 * "fail loudly" rules out stale reads from IDB. Job queue and history are
 * session-scoped realtime state, not a cache to seed the next reload.
 */
const VOLATILE_ROOTS = new Set([
  'auth',
  'user',
  'profile',
  'subscriptions',
  'subscription',
  'payments',
  'payment',
  'job-history',
  'jobs',
  'queue',
]);

export function shouldPersistQuery(query: Query): boolean {
  const root = query.queryKey[0];
  if (typeof root !== 'string') return false;
  if (VOLATILE_ROOTS.has(root)) return false;
  // Only persist queries that have actually loaded data successfully.
  return query.state.status === 'success';
}

/**
 * Wipe the persisted cache entirely. Call on logout so the next user does
 * not inherit the previous user's project/concept/album snapshots.
 */
export async function clearPersistedQueryCache(): Promise<void> {
  if (!idbStore) return;
  await clear(idbStore);
}
